import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useWebSocket } from '../context/WebSocket/WebSocketContext';
import { WEB_SOCKET_CHANNELS } from '../utils';
import { addNotification } from '../store/notification/slice';
import { removePendingBulkTakedowns } from '../store/pendingBulkTakedowns/slice';
import { PendingBulkTakedownItem } from '../store/pendingBulkTakedowns/types';
import { removePendingTakedownStatusUpdates } from '../store/pendingTakedownStatusUpdates/slice';
import { PendingTakedownStatusUpdate } from '../store/pendingTakedownStatusUpdates/types';
import {
  BulkTakedownUpdatePayload,
  TakedownStatusUpdateSocketPayload,
  WebSocketMessage,
} from '../helpers/types';

/** The per-item `error` is a plain string for validation failures or an
 * object like `{ message, existing_takedown }` when a takedown already
 * exists for the item. */
export const getBulkTakedownErrorMessage = (
  error: BulkTakedownUpdatePayload['error'],
): string => {
  if (!error) return 'Takedown could not be created';
  if (typeof error === 'string') return error;
  return error.message || 'Takedown could not be created';
};

/** Closes the feedback loop for bulk takedown initiation (WHEEL-195): the
 * progress modal's toast is ephemeral and its WS subscription dies with it,
 * so items are queued in the pendingBulkTakedowns slice at confirm time and
 * settled here, app-wide, as their `bulk_takedown` results arrive. Failures
 * land in the persisted notifications drawer with the backend's per-item
 * message; successes just clear the pending entry (the modal/toast already
 * covers the happy path). */
export function useBulkTakedownNotifier(): void {
  const dispatch = useDispatch();
  const { subscribe } = useWebSocket();

  const items = useSelector(
    (state: any) =>
      (state.pendingBulkTakedowns?.items ?? []) as PendingBulkTakedownItem[],
  );
  // Latest items in a ref so the subscription stays stable (subscribe once)
  // instead of churning on every queue change.
  const itemsRef = useRef<PendingBulkTakedownItem[]>(items);
  itemsRef.current = items;

  useEffect(() => {
    const handler = (message: WebSocketMessage<BulkTakedownUpdatePayload>) => {
      const data = message?.data;
      if (!data) return;
      const match = itemsRef.current.find(
        item =>
          item.attackId === data.attack_id && item.brandId === data.brand_id,
      );
      if (!match) return;

      // The takedown-service message carries user_id/user_email at the
      // top level, outside the typed { channel, data } envelope.
      const envelope =
        message as WebSocketMessage<BulkTakedownUpdatePayload> & {
          user_id?: string;
          user_email?: string;
        };
      // company_id enables the drawer's threat deep-link; success events
      // (enriched takedown record) and already-initiated errors both carry it.
      const companyId =
        data.company_id ??
        (typeof data.error === 'object'
          ? data.error.existing_takedown?.company_id
          : undefined) ??
        null;
      const base = {
        thread_name: match.threatName,
        userEmail: envelope.user_email || envelope.user_id || '-',
        date: new Date().toISOString(),
        threat_id: data.attack_id,
        company_id: companyId,
        protected_asset_id: data.brand_id,
        protected_asset_name: null,
        user_id: envelope.user_id ?? null,
        operation: 'bulk_takedown',
      };
      if (data.error) {
        // An already-initiated failure carries the pre-existing takedown, so
        // the drawer entry can link straight to it.
        const existingTakedownId =
          typeof data.error === 'object'
            ? data.error.existing_takedown?.id ?? null
            : null;
        dispatch(
          addNotification({
            ...base,
            status: 'takedown failed',
            errorMessage: getBulkTakedownErrorMessage(data.error),
            isError: true,
            takedown_id: existingTakedownId,
          }),
        );
      } else {
        dispatch(
          addNotification({
            ...base,
            status: 'takedown created',
            errorMessage: null,
            isError: false,
            // Success events are the enriched takedown record.
            takedown_id: data.id ?? null,
          }),
        );
      }
      dispatch(
        removePendingBulkTakedowns([
          { attackId: data.attack_id, brandId: data.brand_id },
        ]),
      );
    };

    const unsubscribe = subscribe(WEB_SOCKET_CHANNELS.BULK_TAKEDOWN, handler);
    return unsubscribe;
  }, [subscribe, dispatch]);

  // Bulk status updates (WHEEL-356): same loop-closing for
  // POST v2/takedown/bulk-status results on the takedown_status_update
  // channel, matched by takedown_id against the pending queue.
  const statusItems = useSelector(
    (state: any) =>
      (state.pendingTakedownStatusUpdates?.items ??
        []) as PendingTakedownStatusUpdate[],
  );
  const statusItemsRef = useRef<PendingTakedownStatusUpdate[]>(statusItems);
  statusItemsRef.current = statusItems;

  useEffect(() => {
    const handler = (
      message: WebSocketMessage<TakedownStatusUpdateSocketPayload>,
    ) => {
      const data = message?.data;
      if (!data?.takedown_id) return;
      const match = statusItemsRef.current.find(
        item => item.takedownId === data.takedown_id,
      );
      if (!match) return;

      const envelope =
        message as WebSocketMessage<TakedownStatusUpdateSocketPayload> & {
          user_id?: string;
          user_email?: string;
        };
      const base = {
        thread_name: match.url,
        userEmail: envelope.user_email || envelope.user_id || '-',
        date: new Date().toISOString(),
        threat_id: null,
        company_id: null,
        protected_asset_id: null,
        protected_asset_name: null,
        user_id: envelope.user_id ?? null,
        operation: 'takedown_status_update',
        takedown_id: match.takedownId,
      };
      if (data.error) {
        dispatch(
          addNotification({
            ...base,
            status: 'takedown status update failed',
            errorMessage: data.error,
            isError: true,
          }),
        );
      } else {
        dispatch(
          addNotification({
            ...base,
            status: data.status || match.targetStatus,
            errorMessage: null,
            isError: false,
          }),
        );
      }
      dispatch(removePendingTakedownStatusUpdates([data.takedown_id]));
    };

    const unsubscribe = subscribe(
      WEB_SOCKET_CHANNELS.TAKEDOWN_STATUS_UPDATE,
      handler,
    );
    return unsubscribe;
  }, [subscribe, dispatch]);
}
